import { useState } from 'react'
import { toast } from 'sonner'

import handleFormSubmitError from '@/utils/forms/handleFormSubmitError'

import type { ConfigSourceType } from '../lib/configFormModel'
import type { ConfigFormInput } from '../lib/configFormModel'
import {
  type ConfigStep,
  getConfigSteps,
  getValidateSourceLabel,
} from '../lib/configModalTexts'

type UseConfigStepsParams = {
  sourceType: ConfigSourceType
  sourceStepFields: Array<keyof ConfigFormInput>
  isSourceValidated: boolean
  isStoreMismatched: boolean
  trigger: (fields: Array<keyof ConfigFormInput>) => Promise<boolean>
  onStoreMismatch: () => void
  validateMainForm: () => Promise<unknown>
  validateWholeForm: () => Promise<unknown>
  onLeaveReview: () => void
  hasPricesStep: boolean
  hasPriceSource: () => boolean
  hasColumnConflicts: () => boolean
}

export const PRICE_SOURCE_REQUIRED_MESSAGE =
  'Укажите колонку закупочной цены, колонку РРЦ или колонку цены по сегменту'

export const COLUMN_CONFLICT_MESSAGE =
  'В «Файл и колонки» одна колонка выбрана для разных полей'

export const useConfigSteps = ({
  sourceType,
  sourceStepFields,
  isSourceValidated,
  isStoreMismatched,
  trigger,
  onStoreMismatch,
  validateMainForm,
  validateWholeForm,
  onLeaveReview,
  hasPricesStep,
  hasPriceSource,
  hasColumnConflicts,
}: UseConfigStepsParams) => {
  const [step, setStep] = useState<ConfigStep>('source')
  const stepOptions = getConfigSteps(hasPricesStep)

  const goToStep = (nextStep: ConfigStep) => {
    if (step === 'review' && nextStep !== 'review') onLeaveReview()
    setStep(nextStep)
  }

  const goNext = async () => {
    if (step === 'source') {
      if (!(await trigger(sourceStepFields))) {
        handleFormSubmitError(false)
        return
      }
      if (isStoreMismatched) {
        onStoreMismatch()
        return
      }
      if (!isSourceValidated) {
        toast.error(`Сначала нажмите «${getValidateSourceLabel(sourceType)}»`)
        return
      }
      goToStep('settings')
      return
    }
    if (step === 'settings' && hasPricesStep) {
      if (!hasPriceSource()) {
        toast.error(PRICE_SOURCE_REQUIRED_MESSAGE)
        return
      }
      if (hasColumnConflicts()) {
        toast.error(COLUMN_CONFLICT_MESSAGE)
        return
      }
      if (await validateMainForm()) goToStep('prices')
      return
    }
    if (await validateWholeForm()) goToStep('review')
  }

  const getStepIndex = (value: ConfigStep) =>
    stepOptions.findIndex((item) => item.value === value)

  const changeStep = (nextStep: ConfigStep) => {
    const currentIndex = getStepIndex(step)
    const nextIndex = getStepIndex(nextStep)
    if (nextIndex < currentIndex) goToStep(nextStep)
    else if (nextIndex === currentIndex + 1) void goNext()
  }

  const goBack = () => {
    const previous = stepOptions[getStepIndex(step) - 1]
    if (previous) goToStep(previous.value)
  }

  return { step, stepOptions, goNext, goBack, changeStep }
}
